import { useState, useEffect, useRef, useMemo, useCallback, useContext } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router";
import { DataTable } from "primereact/datatable";
import type { DataTableFilterMeta, DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import type { ColumnSortEvent } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Checkbox } from "primereact/checkbox";
import { Skeleton } from "primereact/skeleton";
import { Tooltip } from "primereact/tooltip";
import { FilterMatchMode } from "primereact/api";
import { ConfirmDialog } from "primereact/confirmdialog";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { getChoresClient, getKnowledgeAssessmentReportClient } from "sj-ui-toolkit/src/client";
import { getProctorClient } from "sj-ui-toolkit/src/client/svcProctor";
import { languageDisplayNames } from "sj-ui-toolkit/src/constants/content";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";

import { AppContext } from "@/context/AppContext";
import CategoriesCarousel from "@/components/reports/assessments/CategoriesCarousel";
import MetricsRow from "@/components/reports/assessments/MetricsRow";
import ProficiencyFilter from "@/components/reports/assessments/ProficiencyFilter";
import ResetButton from "@/components/reports/assessments/ResetButton";
import {
  KA_STATUS_KEYS,
  STATUS_CONFIG,
  type KaStatusKey,
} from "sj-ui-toolkit/src/constants/assessmentStatus";
import {
  COMPLETED_STATUS_CODE,
  STATUS_CODE_MAP,
  LINKABLE_STATUSES,
  type LearnerScore,
  type AssignmentScores,
  type SelfInitiatedReportLearner,
  type ProficiencyDistribution,
} from "@/constants/assessmentsReport.constants";
import {
  addReportingScoreToLearner,
  enrichLearnerRows,
  filterCompletedLearnerRows,
  calculateAssessmentScores,
  calculateAverageTimeSpent,
  calculateProficiencyDistribution,
  applyLearnerFilters,
  handleProficiencyFilterClick,
  filterAllScores,
  calculateAverageScores,
} from "@/helpers/assessmentsReport";
import {
  skeletonRows,
  withSkeleton,
  textBody,
  scoreProgressBody,
  statusBody,
  proficiencyDotBody,
} from "@/helpers/tableBody";
import { textFilterElement, multiSelectFilterElement } from "@/helpers/tableFilters";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StatusOption {
  code: string;
  displayName: string;
}

interface RawLearnerStatus {
  directoryUuid: string;
  email: string;
  givenName?: string;
  familyName?: string;
  overallStatus?: { code?: string; displayName?: string };
  taskStatus?: Array<{ programmingLanguage?: string }>;
  scores?: LearnerScore["scores"];
  dueDate?: string | null;
  completedAt?: string | null;
  timeSpent?: string | null;
  examInstanceId?: string;
  isArchived?: boolean;
  status?: string;
}

type FilterState = {
  global: { value: string | null; matchMode: string };
  email: { value: string | null; matchMode: string };
  givenName: { value: string | null; matchMode: string };
  familyName: { value: string | null; matchMode: string };
  statusCode: { value: string[] | null; matchMode: string };
  proficiency: { value: string[] | null; matchMode: string };
  programmingLanguage: { value: string[] | null; matchMode: string };
};

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const ASSESSMENTS_ROUTE = AdminAppRoutes.routes.reporting.routes.assessments.route;
const IN_PROGRESS_TOOLTIP = "Results available upon completion.";
const NOT_COMPLETED_TOOLTIP = "Assessment not completed.";
const SELF_INITIATED_ID = "self-initiated";
const SELF_INITIATED_EXAM_KEY = "the-exam";

const SELF_INITIATED_STATUS_DISPLAY: Record<string, string> = {
  started: "In Progress",
  submitted: "Completed",
};

const SELF_INITIATED_STATUS_CODE_MAP: Record<string, string> = {
  started: "CODE_STARTED",
  submitted: "CODE_COMPLETED",
};

const ASSESSMENT_USER_ROUTE =
  AdminAppRoutes.routes.reporting.routes.assessmentId.routes.userId.route;
const toUserPath = (assessmentId: string, userId: string) =>
  ASSESSMENT_USER_ROUTE.replace(":id", assessmentId).replace(":user_id", userId);

// ---------------------------------------------------------------------------
// Module-level column body templates (no runtime dependencies)
// ---------------------------------------------------------------------------

const firstNameBody = textBody<LearnerScore>("givenName");
const lastNameBody = textBody<LearnerScore>("familyName");
const timeSpentColumnBody = textBody<LearnerScore>("timeSpentFormatted");
const dueDateBody = withSkeleton<LearnerScore>(row =>
  row.dueDate ? new Date(row.dueDate).toLocaleDateString() : "--"
);
const completedAtBody = withSkeleton<LearnerScore>(row =>
  row.completedAt ? new Date(row.completedAt).toLocaleDateString() : "--"
);
const scoreBody = scoreProgressBody<LearnerScore>("averageScore", themeClasses.bgIndigoDataviz5);
const proficiencyBody = proficiencyDotBody<LearnerScore>("proficiency");
const kaStatusBody = statusBody<LearnerScore>(row => {
  const statusKey = STATUS_CODE_MAP[row.statusCode] as KaStatusKey | undefined;
  const config = statusKey ? STATUS_CONFIG[statusKey] : { icon: "", color: "" };
  const colorClass = config.color === "warning" ? themeClasses.textWarning : config.color;
  const label = row.statusName ?? row.statusCode ?? "--";
  return { icon: config.icon || undefined, colorClass: colorClass || undefined, label };
});
const languageBody = withSkeleton<LearnerScore>(row => {
  if (!row.programmingLanguage) return "--";
  return languageDisplayNames[row.programmingLanguage] ?? row.programmingLanguage;
});

// Proficiency filter options are constant
const PROFICIENCY_FILTER_OPTIONS = [
  { label: "Foundational", value: "foundational" },
  { label: "Intermediate", value: "intermediate" },
  { label: "Advanced", value: "advanced" },
];
const proficiencyFilterElement = multiSelectFilterElement(
  PROFICIENCY_FILTER_OPTIONS,
  "Select Proficiency"
);
const emailTextFilterElement = textFilterElement("Search...");

// Module-level skeleton rows removed — count is now read from persisted table state

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AssessmentReportDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    state: { viewOnly },
  } = useContext(AppContext);
  const { showToast } = useToast();
  const hideTimeSpent = useHideTimeSpent();
  const dt = useRef<DataTable<DataTableValueArray>>(null);

  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const { contentBundles, isRestrictedBundle } = useUserContext();

  const [assessmentTitle, setAssessmentTitle] = useState<string | "loading">("loading");
  const [learnerScores, setLearnerScores] = useState<LearnerScore[] | "loading">("loading");
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [assessmentSettings, setAssessmentSettings] = useState({
    isDynamic: false,
    dynamicDays: null as number | null,
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [includeArchivedUsers, setIncludeArchivedUsers] = useState(false);
  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null);
  const [resettingUuid, setResettingUuid] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const isDownloadingRef = useRef(false);

  const [filters, setFilters] = useState<FilterState>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    givenName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    familyName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    statusCode: { value: null, matchMode: FilterMatchMode.IN },
    proficiency: { value: null, matchMode: FilterMatchMode.IN },
    programmingLanguage: { value: null, matchMode: FilterMatchMode.IN },
  });

  useBreadcrumb(
    assessmentTitle !== "loading"
      ? { label: assessmentTitle, route: `${ASSESSMENTS_ROUTE}/${id ?? ""}`, display: "page" }
      : null
  );

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadSelfInitiated = useCallback(async () => {
    const kaReportClient = getKnowledgeAssessmentReportClient();
    const reportingResponse = await kaReportClient.getScores(currentTenantName, {
      examKey: SELF_INITIATED_EXAM_KEY,
      userInitiated: true,
    });
    if (reportingResponse.error) throw reportingResponse.error;

    const data = reportingResponse.data as AssignmentScores;
    const rawLearners = (data.assignmentScores ?? []).flatMap(
      a => (a.learnerScores ?? []) as SelfInitiatedReportLearner[]
    );

    setStatusOptions(
      Object.entries(SELF_INITIATED_STATUS_CODE_MAP).map(([key, code]) => ({
        code,
        displayName: SELF_INITIATED_STATUS_DISPLAY[key] ?? key,
      }))
    );
    setAssessmentTitle("Self-initiated Assessments");
    setAssessmentSettings({ isDynamic: false, dynamicDays: null });
    setLearnerScores(
      rawLearners.map(l => {
        const status = l.status ?? "";
        return {
          directoryUuid: l.directoryUuid,
          email: l.email,
          givenName: l.givenName,
          familyName: l.familyName,
          scores: l.scores,
          dueDate: l.dueAt,
          completedAt: l.completedAt,
          timeSpent: l.timeSpent,
          examInstanceId: l.examInstanceId,
          isArchived: false,
          status: l.status,
          statusCode: SELF_INITIATED_STATUS_CODE_MAP[status] ?? status,
          statusName: SELF_INITIATED_STATUS_DISPLAY[status] ?? l.status,
          programmingLanguage: l.programmingLanguage ?? null,
        };
      })
    );
  }, [currentTenantName]);

  const loadData = useCallback(async () => {
    if (!id || !currentTenantName) return;

    try {
      if (id === SELF_INITIATED_ID) {
        await loadSelfInitiated();
        return;
      }

      const choresClient = getChoresClient();
      const kaReportClient = getKnowledgeAssessmentReportClient();

      const [assessmentMetadataRaw, reportingResponse, statusesResponse] = await Promise.all([
        choresClient.getAssessment(currentTenantName, id),
        kaReportClient.getScores(currentTenantName, { assignmentId: id }),
        choresClient.getAvailableStatuses(),
      ]);

      // Statuses
      const statusesData = statusesResponse as {
        data?: { items?: StatusOption[] };
        error?: unknown;
      };
      if (!statusesData.error) {
        setStatusOptions(statusesData.data?.items ?? []);
      }

      // Reporting data
      if (reportingResponse.error) throw reportingResponse.error;

      // Assessment metadata — API returns { data: { report: KaStatusReport } }
      const meta = assessmentMetadataRaw as {
        data?: { report?: Record<string, unknown> };
        error?: unknown;
      };
      if (meta.error) throw meta.error;

      const report = meta.data?.report ?? {};

      // Map raw learnerStatus[] → LearnerScore[] with flattened statusCode / programmingLanguage
      const rawLearnerStatuses = (report.learnerStatus as RawLearnerStatus[] | undefined) ?? [];
      const learnerStatuses: LearnerScore[] = rawLearnerStatuses.map(ls => {
        const langs = (ls.taskStatus ?? [])
          .map(t => t.programmingLanguage)
          .filter((lang, i, self): lang is string => !!lang && self.indexOf(lang) === i);
        const programmingLanguage = langs.join(", ") || null;
        return {
          directoryUuid: ls.directoryUuid,
          email: ls.email,
          givenName: ls.givenName,
          familyName: ls.familyName,
          scores: ls.scores,
          dueDate: ls.dueDate,
          completedAt: ls.completedAt,
          timeSpent: ls.timeSpent,
          examInstanceId: ls.examInstanceId,
          isArchived: ls.isArchived,
          status: ls.status,
          statusCode: ls.overallStatus?.code ?? "",
          statusName: ls.overallStatus?.displayName ?? ls.overallStatus?.code,
          programmingLanguage,
        };
      });

      const title = (report.title as string | undefined) ?? "";
      const dynamic = (report.dynamic as boolean | undefined) ?? false;
      const dynamicDays = (report.dynamicDays as number | null | undefined) ?? null;

      setAssessmentTitle(title);
      setAssessmentSettings({ isDynamic: dynamic, dynamicDays });

      const merged = addReportingScoreToLearner(
        { title, dynamic, dynamicDays, learnerStatuses },
        reportingResponse.data as AssignmentScores
      );

      setLearnerScores(merged.learnerStatuses);
    } catch (error) {
      console.error("Failed to load assessment data:", error);
      showToast({ severity: "error", detail: "Failed to load assessment data" });
      setAssessmentTitle("");
      setLearnerScores([]);
    }
  }, [id, currentTenantName]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Apply ?statusFilter= query param once status options are loaded
  useEffect(() => {
    const statusFilter = searchParams.get("statusFilter");
    if (!statusFilter || statusOptions.length === 0) return;

    setActiveFilterKey(statusFilter);

    // "notStarted" filter also captures overdue learners
    const filterKeys: string[] =
      statusFilter === KA_STATUS_KEYS.notStarted
        ? [KA_STATUS_KEYS.notStarted, KA_STATUS_KEYS.overdue]
        : [statusFilter];
    const values = Object.entries(STATUS_CODE_MAP)
      .filter(([, key]) => filterKeys.includes(key))
      .map(([code]) => code);
    if (values.length) {
      setFilters(prev => ({
        ...prev,
        statusCode: { value: values, matchMode: FilterMatchMode.IN },
      }));
    }
  }, [searchParams, statusOptions]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const isLoading = learnerScores === "loading";

  const bundlesLoaded = contentBundles !== null;

  const stateKey = `assessmentReportDetail-${currentTenantName}-${id ?? ""}`;

  const storedRows = useMemo(() => {
    try {
      const stored = window.localStorage.getItem(stateKey);
      return stored
        ? ((JSON.parse(stored) as { rows?: number }).rows ?? DEFAULT_ROW_COUNT)
        : DEFAULT_ROW_COUNT;
    } catch {
      return DEFAULT_ROW_COUNT;
    }
  }, [stateKey]);

  const detailSkeletonRows = useMemo(() => skeletonRows<LearnerScore>(storedRows), [storedRows]);

  const activeUserScores = useMemo<LearnerScore[]>(() => {
    if (isLoading) return [];
    return (learnerScores as LearnerScore[]).filter(u => includeArchivedUsers || !u.isArchived);
  }, [learnerScores, includeArchivedUsers, isLoading]);

  const completeReports = useMemo<LearnerScore[]>(
    () => enrichLearnerRows(activeUserScores),
    [activeUserScores]
  );

  const filteredLearnerScores = useMemo<LearnerScore[]>(
    () => applyLearnerFilters(completeReports, filters),
    [completeReports, filters]
  );

  const availableLanguages = useMemo<string[]>(() => {
    const langs = new Set<string>();
    completeReports.forEach(r => {
      if (r.programmingLanguage) langs.add(r.programmingLanguage);
    });
    return Array.from(langs);
  }, [completeReports]);

  const metrics = useMemo(() => {
    const completed = filterCompletedLearnerRows(filteredLearnerScores);
    const avgScores = filteredLearnerScores
      .map(r => r.averageScore)
      .filter((s): s is number => s != null);
    const completionPct =
      filteredLearnerScores.length > 0
        ? Math.round((completed.length / filteredLearnerScores.length) * 100)
        : 0;
    const avg = calculateAssessmentScores(avgScores);
    return {
      averageScore: avg === "--" ? "--" : `${avg}%`,
      ...(!hideTimeSpent ? { averageTimeSpent: calculateAverageTimeSpent(completed) } : {}),
      completionRate: `${completionPct}%`,
    };
  }, [filteredLearnerScores, hideTimeSpent]);

  const { categoryScoresAverage, topicScoresAverage } = useMemo(() => {
    if (filteredLearnerScores.length === 0)
      return { categoryScoresAverage: [], topicScoresAverage: {} };
    const filtered = filterAllScores(filteredLearnerScores);
    return calculateAverageScores(filtered);
  }, [filteredLearnerScores]);

  const proficiencyDistribution = useMemo<ProficiencyDistribution>(
    () => calculateProficiencyDistribution(completeReports),
    [completeReports]
  );

  const anyCompletions = useMemo(
    () => completeReports.some(r => r.statusCode === COMPLETED_STATUS_CODE),
    [completeReports]
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleReset = useCallback(
    async (rowData: LearnerScore) => {
      if (!id) return;
      if (resettingUuid) return;
      if (rowData.statusCode !== COMPLETED_STATUS_CODE && !rowData.examInstanceId) {
        showToast({ severity: "error", detail: "Cannot reset: exam instance not found." });
        return;
      }
      setResettingUuid(rowData.directoryUuid);
      try {
        if (rowData.statusCode === COMPLETED_STATUS_CODE) {
          const res = await getChoresClient().resetAssignmentProgress(id, rowData.directoryUuid);
          const r = res as { error?: unknown };
          if (r.error) throw r.error;
        } else {
          // examInstanceId is guaranteed non-empty by the guard above
          const { error } = await getProctorClient().stopExam(
            "the-exam",
            rowData.directoryUuid,
            rowData.examInstanceId!
          );
          if (error) throw error;
        }
        showToast({ severity: "success", detail: "Learner's assessment has been reset." });
        // Reload
        setLearnerScores("loading");
        void loadData();
      } catch (err) {
        const msg =
          (err as { data?: { message?: string }; message?: string })?.data?.message ??
          (err as { message?: string })?.message ??
          "Unknown error";
        console.error("Reset assessment error:", err);
        showToast({ severity: "error", detail: `Failed to reset assessment: ${msg}` });
      } finally {
        setResettingUuid(null);
      }
    },
    [id, resettingUuid, showToast, loadData]
  );

  const handleGenerateAssignment = useCallback(() => {
    const usersToInclude =
      filteredLearnerScores.length > 0 ? filteredLearnerScores : completeReports;
    const userIds = usersToInclude.map(r => r.directoryUuid);
    if (userIds.length && id) {
      sessionStorage.setItem("currentRecommendedAssignment", id);
      sessionStorage.setItem("prepopulatedUserIds", JSON.stringify(userIds));
      sessionStorage.setItem(
        "assessmentScoreData",
        JSON.stringify({
          categoryScores: categoryScoresAverage,
          topicScores: topicScoresAverage,
          learnerScores: usersToInclude,
        })
      );
    }
    if (id) {
      navigate(
        AdminAppRoutes.routes.assessments.routes.recommendedAssignment.route.replace(
          ":assessmentId",
          id
        )
      );
    }
  }, [
    filteredLearnerScores,
    completeReports,
    id,
    categoryScoresAverage,
    topicScoresAverage,
    navigate,
  ]);

  const exportCSV = useCallback(() => {
    dt.current?.exportCSV({ selectionOnly: false });
  }, []);

  const downloadFullResults = useCallback(async () => {
    if (!currentTenantName || !id) return;
    if (isDownloadingRef.current) return;
    isDownloadingRef.current = true;
    setIsDownloading(true);
    try {
      const kaReportClient = getKnowledgeAssessmentReportClient();
      const req =
        id === SELF_INITIATED_ID
          ? { examKey: SELF_INITIATED_EXAM_KEY, userInitiated: true as const }
          : { assignmentId: id };
      await kaReportClient.downloadScoresCsv(currentTenantName, req);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? "Unknown error";
      showToast({ severity: "error", detail: `Failed to download results: ${msg}` });
    } finally {
      isDownloadingRef.current = false;
      setIsDownloading(false);
    }
  }, [id, currentTenantName, showToast]);

  const onGlobalFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, global: { ...prev.global, value } }));
    setGlobalFilterValue(value);
  }, []);

  const onFilterChange = useCallback((e: { filters: unknown }) => {
    setFilters(e.filters as FilterState);
  }, []);

  const handleProficiencyClick = useCallback(
    (key: string) => {
      handleProficiencyFilterClick(
        key,
        activeFilterKey,
        setActiveFilterKey,
        setFilters as React.Dispatch<
          React.SetStateAction<Record<string, { value: unknown; matchMode: string }>>
        >,
        FilterMatchMode.IN
      );
    },
    [activeFilterKey]
  );

  // ---------------------------------------------------------------------------
  // Column body templates (runtime deps only)
  // ---------------------------------------------------------------------------

  const emailBody = useCallback(
    (rowData: LearnerScore | null) => {
      if (rowData == null) return <Skeleton />;
      const isLinkable = LINKABLE_STATUSES.includes(rowData.statusCode);
      const hasScore = rowData.averageScore != null;
      if (!isLinkable || !hasScore) {
        const tooltipId = `email-tooltip-${rowData.directoryUuid}-${rowData.examInstanceId ?? ""}`;
        const tooltipText = isLinkable ? NOT_COMPLETED_TOOLTIP : IN_PROGRESS_TOOLTIP;
        return (
          <>
            <span id={tooltipId} className="cursor-default">
              {rowData.email}
            </span>
            <Tooltip target={`#${tooltipId}`} position="right">
              {tooltipText}
            </Tooltip>
          </>
        );
      }
      if (!id) return rowData.email;

      const userPath =
        id === SELF_INITIATED_ID && rowData.examInstanceId
          ? `${toUserPath(id, rowData.directoryUuid)}?examInstanceId=${rowData.examInstanceId}`
          : toUserPath(id, rowData.directoryUuid);

      return (
        <Link to={userPath} className={themeClasses.textPrimaryDarker}>
          {rowData.email}
        </Link>
      );
    },
    [id]
  );

  const actionBody = useCallback(
    (rowData: LearnerScore | null) =>
      rowData == null ? (
        <Skeleton />
      ) : (
        <ResetButton
          rowData={rowData}
          viewOnly={viewOnly}
          handleReset={handleReset}
          isResetting={resettingUuid === rowData.directoryUuid}
        />
      ),
    [viewOnly, handleReset, resettingUuid]
  );

  // ---------------------------------------------------------------------------
  // Filter element templates (runtime deps only)
  // ---------------------------------------------------------------------------

  const statusFilterElement = useMemo(
    () =>
      multiSelectFilterElement(
        statusOptions.map(s => ({ label: s.displayName, value: s.code })),
        "Select Status"
      ),
    [statusOptions]
  );

  const languageFilterElement = useMemo(
    () =>
      multiSelectFilterElement(
        availableLanguages
          .map(lang => ({ label: languageDisplayNames[lang] ?? lang, value: lang }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        "Select Language"
      ),
    [availableLanguages]
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <ConfirmDialog />

      {/* Page header */}
      <div className="flex justify-between items-center mb-4">
        {isLoading ? <Skeleton width="200px" height="2rem" /> : <h1>{assessmentTitle}</h1>}
        {id !== SELF_INITIATED_ID && (
          <span id="generate-assignment-wrapper">
            <Button
              id="generate-assignment-button"
              label="Generate Assignment"
              onClick={handleGenerateAssignment}
              disabled={isLoading || viewOnly || !anyCompletions || isRestrictedBundle}
            />
            {!anyCompletions && !isLoading && !isRestrictedBundle && (
              <Tooltip
                target="#generate-assignment-wrapper"
                position="left"
                className="max-w-[300px]"
              >
                Assignment recommendations will be available when learners complete this Assessment.
              </Tooltip>
            )}
            {bundlesLoaded && isRestrictedBundle && (
              <Tooltip
                target="#generate-assignment-wrapper"
                position="left"
                className="max-w-[300px]"
              >
                To have custom training plans built to meet your team's needs, please upgrade to an
                Enterprise account.
              </Tooltip>
            )}
          </span>
        )}
      </div>

      {/* Metrics */}
      <MetricsRow metrics={metrics} isLoading={isLoading} />
      <hr className={`mb-12 ${themeClasses.borderSurface300}`} />

      {/* Proficiency filter bar */}
      <ProficiencyFilter
        proficiencyDistribution={proficiencyDistribution}
        activeFilterKey={activeFilterKey}
        onFilterClick={handleProficiencyClick}
        onClearFilter={() => {
          setActiveFilterKey(null);
          setFilters(prev => ({
            ...prev,
            statusCode: { value: null, matchMode: FilterMatchMode.IN },
            proficiency: { value: null, matchMode: FilterMatchMode.IN },
          }));
        }}
      />

      {/* Category scores carousel */}
      <CategoriesCarousel
        categoryScores={categoryScoresAverage}
        topicScores={topicScoresAverage}
        isLoading={isLoading}
        hasFilteredData={filteredLearnerScores.length > 0}
      />

      {/* DataTable */}
      <div>
        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
          <div className="flex gap-2 items-center">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={globalFilterValue}
                onChange={onGlobalFilterChange}
                placeholder="Search users..."
              />
            </IconField>
            <div className="flex items-center gap-2">
              <Checkbox
                inputId="archive"
                checked={includeArchivedUsers}
                onChange={e => setIncludeArchivedUsers(e.checked ?? false)}
              />
              <label htmlFor="archive">Include archived users</label>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              label="Export data"
              icon="pi pi-download"
              onClick={exportCSV}
              disabled={isLoading}
            />
            <Button
              label="Download Full Results"
              icon="pi pi-file-export"
              onClick={() => void downloadFullResults()}
              disabled={isLoading || isDownloading}
              loading={isDownloading}
            />
          </div>
        </div>

        <DataTable
          ref={dt}
          aria-label="Assessment results"
          value={
            (isLoading
              ? detailSkeletonRows
              : filteredLearnerScores) as unknown as DataTableValueArray
          }
          paginator
          rows={storedRows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          stateKey={stateKey}
          stateStorage="local"
          sortField="email"
          sortOrder={1}
          removableSort
          filters={isLoading ? undefined : (filters as DataTableFilterMeta)}
          onFilter={onFilterChange}
          globalFilterFields={isLoading ? [] : ["email", "givenName", "familyName"]}
          tableClassName="dt-responsive-text"
          emptyMessage="No assessment results found."
        >
          <Column
            field="email"
            header="Email"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={emailTextFilterElement}
            body={emailBody}
            style={{ minWidth: "16rem" }}
          />
          <Column
            field="givenName"
            header="First"
            sortable
            filter
            showFilterMatchModes={false}
            body={firstNameBody}
          />
          <Column
            field="familyName"
            header="Last"
            sortable
            filter
            showFilterMatchModes={false}
            body={lastNameBody}
          />
          <Column
            field="statusCode"
            header="Status"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={statusFilterElement}
            body={kaStatusBody}
            exportField="statusName"
            key={`status-filter-${statusOptions.length}`}
          />
          <Column
            field="programmingLanguage"
            header="Assessment Language"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={languageFilterElement}
            body={languageBody}
          />
          <Column field="averageScore" header="Score" sortable body={scoreBody} />
          {assessmentSettings.isDynamic && (
            <Column field="dueDate" header="Due Date" sortable body={dueDateBody} />
          )}
          <Column
            field="proficiency"
            header="Proficiency"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={proficiencyFilterElement}
            body={proficiencyBody}
          />
          {!hideTimeSpent && (
            <Column
              field="timeSpentFormatted"
              header="Time Spent"
              sortable
              sortFunction={(e: ColumnSortEvent) => {
                const { data, order } = e;
                const o = order ?? 1;
                return [...(data as LearnerScore[])].sort((a, b) => {
                  if (a == null || b == null) return a == null ? 1 : -1;
                  const aV = a.timeSpentSeconds ?? -1;
                  const bV = b.timeSpentSeconds ?? -1;
                  return (aV - bV) * o;
                });
              }}
              body={timeSpentColumnBody}
            />
          )}
          <Column field="completedAt" header="Date Completed" sortable body={completedAtBody} />
          {id !== SELF_INITIATED_ID && <Column header="Action" body={actionBody} />}
        </DataTable>
      </div>
    </>
  );
}
